// src/onboarding/activation/kit/soundFX.js

/**
 * Centralized sound + praise for FinTorro lessons (no external assets).
 * Exports:
 *   - ensureUserAudioOnce, playTone, playHintPing, playMissThunk, playSuccessArp
 *   - PRAISES
 *   - speak(text)
 *   - speakRandomPraise()
 *   - celebrateSuccess()
 */

// src/onboarding/activation/kit/soundFX.js
/**
 * Centralized sound + praise for FinTorro lessons (no external assets).
 * Chrome-safe SpeechSynthesis: arming + pre-warm + stable voice selection.
 */

// src/onboarding/activation/kit/soundFX.js
let _AC;
function getAC() {
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    if (!_AC) _AC = new Ctor();
    return _AC;
  } catch { return null; }
}

/** Resume WebAudio once after a user gesture (Chrome/iOS autoplay policy). */
export function ensureUserAudioOnce() {
  if (typeof window === "undefined") return;
  if (window.__ftAudioArmed) return;

  const arm = async () => {
    try {
      const ac = getAC();
      if (ac && ac.state !== "running") await ac.resume();
    } catch {}
    window.__ftAudioArmed = true;
    ensureSpeechOnce(); // arm TTS too
  };

  window.addEventListener("pointerdown", arm, { once: true, capture: true });
  window.addEventListener("keydown", arm,    { once: true, capture: true });
  window.addEventListener("touchstart", arm, { once: true, capture: true });
}

/* ---------------- Low-level SFX ---------------- */
export function playTone({ freq = 880, dur = 0.12, type = "sine", vol = 0.15 } = {}) {
  try {
    const ac = getAC(); if (!ac) return;
    if (ac.state !== "running") ac.resume();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = type; osc.frequency.value = freq; gain.gain.value = vol;
    osc.connect(gain).connect(ac.destination);
    osc.start(); osc.stop(ac.currentTime + dur);
    osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch {} };
  } catch {}
}
export function playHintPing() { playTone({ freq: 980, dur: 0.06, vol: 0.12 }); }
export function playMissThunk() {
  playTone({ freq: 240, dur: 0.10, type: "triangle", vol: 0.18 });
  setTimeout(() => playTone({ freq: 180, dur: 0.08, type: "triangle", vol: 0.16 }), 110);
}

/* --- debounce guard for the success arp (fallback) --- */
const DEDUPE_MS = 1200;
let _lastArpAt = 0;
export function playSuccessArp() {
  const now = Date.now();
  if (now - _lastArpAt < DEDUPE_MS) return; // prevent double-arp
  _lastArpAt = now;
  playTone({ freq: 660, dur: 0.08 });
  setTimeout(() => playTone({ freq: 880, dur: 0.08 }), 90);
  setTimeout(() => playTone({ freq: 1320, dur: 0.12 }), 180);
}

/* ---------------- Praise lines ---------------- */
export const PRAISES = [
  "Awesome!", "Amazing!", "Great job!", "Well done!", "Nice work!", "Superb!",
  "You’re flying!", "Bullish move!", "So clean!", "Textbook!", "On point!",
  "Nailed it!", "Perfect!", "Love it!", "Crushed it!", "That’s it!", "Beautiful!",
  "Right there!", "Spot on!", "Sharp move!",
];
function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

/* ---------------- Chrome-safe SpeechSynthesis ---------------- */
let _ttsArmed = false;
let _voiceCache = null;
let _voicesObserved = false;
let _queue = Promise.resolve(); // serialize speaks

// dedupe: avoid speaking the same phrase twice within a short window
let _lastSpeak = { t: 0, text: "" };

const isChrome = () => {
  const ua = navigator.userAgent || "";
  return /Chrome/i.test(ua) && !/Edg|OPR/i.test(ua);
};

function choosePreferredVoice() {
  try {
    const synth = window.speechSynthesis;
    const list = synth?.getVoices?.() || [];
    if (!list.length) return null;

    // Prefer Google English on Chrome for consistency
    const googleEn = list.find(v => /google/i.test(v.name) && /^en[-_]/i.test(v.lang || ""));
    if (googleEn) return googleEn;

    const anyEn = list.find(v => /^en[-_]/i.test(v.lang || ""));
    if (anyEn) return anyEn;

    return list[0] || null;
  } catch { return null; }
}

function observeVoicesOnce() {
  try {
    const synth = window.speechSynthesis;
    if (!synth || _voicesObserved) return;
    _voicesObserved = true;

    const update = () => { _voiceCache = choosePreferredVoice(); };
    synth.addEventListener?.("voiceschanged", update);
    // Nudge population
    try { synth.getVoices(); } catch {}
    // Try an immediate cache fill too
    _voiceCache = _voiceCache || choosePreferredVoice();
  } catch {}
}

/** Warm up TTS with a short, nearly-inaudible phrase (not too tiny). */
function warmUpTTS() {
  const synth = window.speechSynthesis;
  if (!synth) return;

  observeVoicesOnce();
  try { synth.resume(); } catch {}

  // IMPORTANT: do not use empty/whitespace; Chrome may drop it.
  // Use a very short word at very low volume to open the pipe.
  const u = new SpeechSynthesisUtterance("init");
  u.volume = 0.05;        // low but audible threshold so Chrome doesn't discard
  u.rate = 1.0; u.pitch = 1.0;
  u.lang = (navigator.language || "en-US");
  const v = _voiceCache || choosePreferredVoice();
  if (v) u.voice = v;

  // Avoid calling cancel() here — it can nuke the next utterance on Chrome.
  u.onend = () => { /* warmed */ };
  // Queue in microtask to stay within gesture without racing pointer handlers
  queueMicrotask(() => {
    try { synth.speak(u); } catch {}
  });
}

function ensureSpeechOnce() {
  if (typeof window === "undefined" || _ttsArmed) return;
  _ttsArmed = true;

  const arm = () => {
    try { warmUpTTS(); } catch {}
  };

  window.addEventListener("pointerdown", arm, { once: true, capture: true });
  window.addEventListener("keydown",    arm, { once: true, capture: true });
  window.addEventListener("touchstart", arm, { once: true, capture: true });
}

/** Speak with queue; avoid cancel()-then-speak which Chrome drops.
 *  Also DEDUPES identical text within DEDUPE_MS to prevent double praise.
 */
async function speakQueued(text, opts = {}) {
  if (!("speechSynthesis" in window)) return false;

  // ---- DEDUPE: identical text within window? pretend success, do nothing.
  const now = Date.now();
  const key = (typeof text === "string" ? text.trim() : "");
  if (key && key === _lastSpeak.text && (now - _lastSpeak.t) < DEDUPE_MS) {
    return true;
  }
  _lastSpeak = { t: now, text: key };

  ensureUserAudioOnce();
  ensureSpeechOnce();
  observeVoicesOnce();

  const synth = window.speechSynthesis;

  const speakOnce = () => new Promise((resolve) => {
    try { synth.resume(); } catch {}
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;
    u.lang = opts.lang || navigator.language || "en-US";
    const v = _voiceCache || choosePreferredVoice();
    if (v) u.voice = v;

    let started = false;
    u.onstart = () => { started = true; };
    u.onend   = () => resolve(true);
    u.onerror = () => resolve(false);

    // If Chrome is currently speaking something (rare), wait a tick
    if (synth.speaking) {
      const wait = () => {
        if (!synth.speaking) {
          synth.removeEventListener?.("end", wait);
          try { synth.speak(u); } catch {}
        } else {
          setTimeout(wait, 50);
        }
      };
      wait();
    } else {
      try { synth.speak(u); } catch { resolve(false); return; }
    }

    // If 'start' never fired (Chrome quirk), retry once after 250ms
    setTimeout(() => {
      if (!started) {
        try { /* no cancel — can drop next utterance */ } catch {}
        const u2 = new SpeechSynthesisUtterance(text);
        u2.rate = 1.0; u2.pitch = 1.0; u2.volume = 1.0; u2.lang = u.lang;
        const v2 = _voiceCache || choosePreferredVoice();
        if (v2) u2.voice = v2;
        u2.onend = () => resolve(true);
        u2.onerror = () => resolve(false);
        try { synth.speak(u2); } catch { resolve(false); }
      }
    }, 250);
  });

  // Serialize all speak calls (Chrome can glitch with overlapping calls)
  _queue = _queue.then(speakOnce, speakOnce);
  return _queue;
}

/* ---------------- Public API ---------------- */
export async function speak(text) {
  const ok = await speakQueued(text);
  if (!ok) playSuccessArp(); // fallback beep (debounced)
  return ok;
}
export function speakRandomPraise() {
  const phrase = pickRandom(PRAISES);
  // Use the queued + deduped speak; only one fallback arp will play if needed.
  speakQueued(phrase).then(ok => { if (!ok) playSuccessArp(); });
  return phrase;
}
export function celebrateSuccess() { return speakRandomPraise(); }










// /**
//  * File: src/kit/soundFX.js
//  * Purpose: Centralized sound + praise for FinTorro lessons (no external assets).
//  * Exports:
//  *   - playTone, playHintPing, playMissThunk, playSuccessArp
//  *   - PRAISES (array of ≤3-word phrases)
//  *   - speak(text)  -> speak a specific message with Speech Synthesis (fallback: arp)
//  *   - speakRandomPraise() -> speak a random phrase from PRAISES; returns the phrase used
//  *   - celebrateSuccess()  -> convenience: speaks random praise + returns it
//  *
//  * Notes:
//  * - All functions are safe no-ops when WebAudio / SpeechSynthesis are blocked.
//  * - Call success *after* a user gesture to avoid autoplay policies.
//  * - Keep UI message text separate; these functions only play audio and return chosen text if needed.
//  */

// // ---------- Low-level beep ----------
// export function playTone({ freq = 880, dur = 0.12, type = "sine", vol = 0.15 } = {}) {
//     try {
//       const AC = window.AudioContext || window.webkitAudioContext;
//       if (!AC) return;
//       const ctx = new AC();
//       const osc = ctx.createOscillator();
//       const gain = ctx.createGain();
//       osc.type = type;
//       osc.frequency.value = freq;
//       gain.gain.value = vol;
//       osc.connect(gain).connect(ctx.destination);
//       osc.start();
//       osc.stop(ctx.currentTime + dur);
//       osc.onended = () => ctx.close();
//     } catch {}
//   }
  
//   // ---------- Stock SFX ----------
//   export function playHintPing() {
//     playTone({ freq: 980, dur: 0.06, vol: 0.12 });
//   }
//   export function playMissThunk() {
//     playTone({ freq: 240, dur: 0.10, type: "triangle", vol: 0.18 });
//     setTimeout(() => playTone({ freq: 180, dur: 0.08, type: "triangle", vol: 0.16 }), 110);
//   }
//   export function playSuccessArp() {
//     playTone({ freq: 660, dur: 0.08 });
//     setTimeout(() => playTone({ freq: 880, dur: 0.08 }), 90);
//     setTimeout(() => playTone({ freq: 1320, dur: 0.12 }), 180);
//   }


  

  
  
//   // ---------- Praise lines (≤ 3 words) ----------
//   export const PRAISES = [
//     "Awesome!",
//     "Amazing!",
//     "Great job!",
//     "Well done!",
//     "Nice work!",
//     "Superb!",
//     "You’re flying!",
//     "Bullish move!",
//     "So clean!",
//     "Textbook!",
//     "On point!",
//     "Nailed it!",
//     "Perfect!",
//     "Love it!",
//     "Crushed it!",
//     "That’s it!",
//     "Beautiful!",
//     "Right there!",
//     "Spot on!",
//     "Sharp move!",
//   ];
  
//   // ---------- Speech Synthesis helpers ----------
//   export function speak(text) {
//     try {
//       if (!("speechSynthesis" in window)) throw new Error("No TTS");
//       const u = new SpeechSynthesisUtterance(text);
//       u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;
//       // Try to pick a voice if available (optional)
//       const vs = window.speechSynthesis.getVoices();
//       if (vs && vs.length) u.voice = vs[0];
//       window.speechSynthesis.cancel();
//       window.speechSynthesis.speak(u);
//       return true;
//     } catch {
//       // fallback beeps if TTS unavailable
//       playSuccessArp();
//       return false;
//     }
//   }
  
//   function pickRandom(arr) {
//     return arr[Math.floor(Math.random() * arr.length)];
//   }
  
//   /**
//    * Speaks a random praise from PRAISES.
//    * Returns the phrase chosen (so you can also show it in text if you want).
//    */
//   export function speakRandomPraise() {
//     const phrase = pickRandom(PRAISES);
//     speak(phrase);
//     return phrase;
//   }
  
//   /**
//    * One-call success helper: speak a random praise and return it.
//    * Usage: const phrase = celebrateSuccess(); setMessage(`🎉 ${phrase} +20 XP …`)
//    */
//   export function celebrateSuccess() {
//     return speakRandomPraise();
//   }
  